import cc from 'classcat';
import { useRef, useId, useEffect } from 'react';
import { PencilIcon } from '@ui/kit/icons';
import { useComponentEntry } from '@utils/component-framework';
import '@i-ark/models';
import { useChangeTableCellEntryMutation } from '../../../state/mutations/entries/change.table-cell.js';
import '@tanstack/react-query';
import { selectLastComponentEditorVersion } from '../../../state/selectors/data.last-editor-version.js';
import { selectTableCellEntryById } from '../../../state/selectors/entry.table-cell-by-id.js';
import { iArkStore } from '../../../state/store.js';
import { useRichTextEditor, getRichTextEditor } from '../hooks/useRichTextEditor.js';
import { useSheetScale } from '../hooks/useSheetScale.js';
import { scrollContainerCls, placeholderIconCls, inputCls, containerCls } from './TableCellInput.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const TableCellInput = ({
  pageId,
  itemId,
  cell,
  sheetRef
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const maxHeight = useRef(undefined);
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const changeTableCellEntry = useChangeTableCellEntryMutation(entryKey);
  const scale = useSheetScale(sheetRef);
  const zoom = iArkStore.useSelector(state => state.zoom);
  const fontScaleRef = useRef(1);
  const placeholder = stripHtmlEntities(cell.placeholder);
  const lastEditorVersion = iArkStore.useSelector(selectLastComponentEditorVersion);
  const cellEntry = useComponentEntry(entryKey, {
    select: selectTableCellEntryById(pageId, itemId, cell.id)
  });
  const prevEntry = useRef(cellEntry.data);
  const editorId = useId();
  const handleChange = value => {
    const editor = getRichTextEditor(editorId);
    const container = containerRef.current;
    if (editor === undefined || container === null) {
      return;
    }
    if (maxHeight.current === undefined) {
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
    }
    if (maxHeight.current !== undefined) {
      container.style.overflow = "hidden";
      if (lastEditorVersion === 1) {
        // iteratively scale font, until we can just fit the entire text content
        // (or reach the predefined maximum/minimum font size)
        while (container.scrollHeight <= maxHeight.current && fontScaleRef.current < 0.95) {
          fontScaleRef.current += 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
        while (container.scrollHeight > maxHeight.current && fontScaleRef.current > 0.15) {
          fontScaleRef.current -= 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
      }

      // if we still cannot fit the available text, then also crop the entry so
      // scrollbars are never added to the field
      if (container.scrollHeight > maxHeight.current) {
        // reset change if content has become taller than the container, as we
        // will not accept scrolling
        editor.el.innerHTML = prevEntry.current ?? "";
        if ((prevEntry.current?.length ?? 0) > 0) {
          // set cursor at the end of editor when resetting content
          const range = document.createRange();
          range.setStart(editor.el, editor.el.childNodes.length);
          range.collapse(true);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        return;
      }
      container.style.overflow = "";
    }

    // cache previous value in case we need to reset to prevent scrolling later
    // on
    if (typeof prevEntry.current !== "undefined" || value) {
      prevEntry.current = value;
    }
    changeTableCellEntry({
      pageId,
      itemId,
      cellId: cell.id,
      value
    });
  };
  useRichTextEditor({
    editorId,
    entryKey,
    ref: inputRef,
    placeholder,
    value: cellEntry.data ?? "",
    onChange: handleChange,
    onReady: () => {
      const editor = getRichTextEditor(editorId);
      const container = containerRef.current;
      if (editor === undefined || container === null) {
        return;
      }
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
      if (maxHeight.current !== undefined && lastEditorVersion === 1) {
        // iteratively scale font, until we can just fit the entire text content
        // (or reach the predefined maximum/minimum font size)
        while (container.scrollHeight > maxHeight.current && fontScaleRef.current > 0.15) {
          fontScaleRef.current -= 0.1;
          container.style.fontSize = `${fontScaleRef.current}em`;
        }
      }
    },
    onFocus: () => {
      const editor = getRichTextEditor(editorId);
      const container = containerRef.current;
      if (editor === undefined || container === null) {
        return;
      }
      updateMaxHeight({
        maxHeight,
        editor,
        container
      });
    }
  });

  // auto-udpate max height whenever zoom level is changed
  useEffect(() => {
    const editor = getRichTextEditor(editorId);
    const container = containerRef.current;
    if (editor === undefined || container === null) {
      return;
    }
    updateMaxHeight({
      maxHeight,
      editor,
      container
    });
    requestAnimationFrame(() => {
      editor.placeholder?.refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, scale]);
  return /*#__PURE__*/jsx("div", {
    className: cc([containerCls, {
      empty: (cellEntry.data ?? "").length === 0
    }]),
    style: {
      fontSize: `${fontScaleRef.current}em`
    },
    children: /*#__PURE__*/jsxs("div", {
      ref: containerRef,
      className: scrollContainerCls,
      children: [/*#__PURE__*/jsx(PencilIcon, {
        className: cc([placeholderIconCls, "pencil"])
      }), /*#__PURE__*/jsx("textarea", {
        ref: inputRef,
        className: inputCls,
        placeholder: placeholder,
        defaultValue: cellEntry.data ?? ""
      })]
    })
  });
};
function updateMaxHeight({
  maxHeight,
  editor,
  container
}) {
  if (!editor.el) {
    return;
  }
  const prevValue = editor.el.innerHTML;
  editor.el.innerHTML = "x";
  maxHeight.current = Math.max(container.clientHeight, container.scrollHeight);
  editor.el.innerHTML = prevValue;
}
function stripHtmlEntities(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html.replace(/<[^>]*>/g, "");
  return txt.value;
}

export { TableCellInput };
//# sourceMappingURL=TableCellInput.js.map
